'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useWallet } from '@/lib/wallet/WalletContext';
import { fetchClearinghouseState } from '@/lib/hyperliquid/service';
import type { HLAssetPosition } from '@/lib/hyperliquid/types';

interface Position {
  id: string;
  symbol: string;
  side: 'Long' | 'Short';
  size: string;
  entryPrice: string;
  markPrice: string;
  pnl: string;
  pnlPct: string;
  positive: boolean;
  leverage: string;
  liquidation: string;
}

function normalizePosition(pos: HLAssetPosition, idx: number): Position {
  const p = pos.position;
  const szi = parseFloat(p.szi);
  const side: 'Long' | 'Short' = szi >= 0 ? 'Long' : 'Short';
  const pnl = parseFloat(p.unrealizedPnl);
  const entryPx = parseFloat(p.entryPx);
  const pnlPct = entryPx > 0 ? ((pnl / (Math.abs(szi) * entryPx)) * 100) : 0;
  const liqPx = p.liquidationPx ? parseFloat(p.liquidationPx) : null;

  return {
    id: `pos-${idx}-${p.coin}`,
    symbol: `${p.coin}/USDC`,
    side,
    size: Math.abs(szi).toFixed(4),
    entryPrice: entryPx.toFixed(2),
    markPrice: parseFloat(p.positionValue) > 0
      ? (parseFloat(p.positionValue) / Math.abs(szi)).toFixed(2)
      : entryPx.toFixed(2),
    pnl: (pnl >= 0 ? '+' : '') + '$' + Math.abs(pnl).toFixed(2),
    pnlPct: (pnlPct >= 0 ? '+' : '') + pnlPct.toFixed(2) + '%',
    positive: pnl >= 0,
    leverage: `${p.leverage?.value ?? 1}x`,
    liquidation: liqPx ? liqPx.toFixed(2) : '—',
  };
}

export default function OpenPositions() {
  const { isConnected, walletAddress } = useWallet();
  const [positions, setPositions] = useState<Position[]>([]);
  const [closingId, setClosingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadPositions = useCallback(async () => {
    if (!isConnected || !walletAddress) {
      setPositions([]);
      return;
    }
    try {
      const state = await fetchClearinghouseState(walletAddress);
      const active = (state.assetPositions ?? []).filter(
        (p) => parseFloat(p.position.szi) !== 0
      );
      setPositions(active.map(normalizePosition));
    } catch {
      // silently ignore
    }
  }, [isConnected, walletAddress]);

  useEffect(() => {
    setIsLoading(true);
    loadPositions().finally(() => setIsLoading(false));
    // Poll every 5 seconds
    const interval = setInterval(loadPositions, 5000);
    return () => clearInterval(interval);
  }, [loadPositions]);

  const handleClose = (id: string) => {
    setClosingId(id);
    // Optimistically remove; real close order would be placed via TradingPanel
    setTimeout(() => {
      setPositions(prev => prev.filter(p => p.id !== id));
      setClosingId(null);
    }, 800);
  };

  if (!isConnected) {
    return (
      <div className="bg-[#1a1a1a] border-t border-[#2a2a2a] p-6 text-center">
        <Icon name="WalletIcon" size={32} className="text-[#2D2D3D] mx-auto mb-2" />
        <p className="text-sm text-[#94A3B8]">Connect wallet to view positions</p>
      </div>
    );
  }

  if (isLoading && positions.length === 0) {
    return (
      <div className="bg-[#1a1a1a] border-t border-[#2a2a2a] p-6 text-center">
        <div className="w-6 h-6 border-2 border-[#4c6ef4] border-t-transparent animate-spin mx-auto mb-2" />
        <p className="text-sm text-[#94A3B8]">Loading positions…</p>
      </div>
    );
  }

  if (positions.length === 0) {
    return (
      <div className="bg-[#1a1a1a] border-t border-[#2a2a2a] p-6 text-center">
        <Icon name="ChartBarIcon" size={32} className="text-[#2D2D3D] mx-auto mb-2" />
        <p className="text-sm text-[#94A3B8]">No open positions</p>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1a1a] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2a2a2a] bg-[#1e1e1e]">
        <span className="text-sm font-semibold text-[#F1F5F9]">Open Positions</span>
        <span className="text-xs bg-[rgba(76,110,244,0.12)] text-[#4c6ef4] px-2 py-0.5">{positions.length}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[#2a2a2a] bg-[#1e1e1e]">
              {['Symbol', 'Side', 'Size', 'Entry', 'Mark', 'Liq.', 'PnL', 'Action'].map(h => (
                <th key={h} className={`px-3 py-2 text-[10px] text-[#94A3B8] uppercase tracking-wider font-medium ${h === 'Action' ? 'text-right' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {positions.map(pos => (
              <tr
                key={pos.id}
                className="border-b border-[#2a2a2a] transition-colors"
                style={{ background: '#242424' }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(76,110,244,0.06)')}
                onMouseLeave={e => (e.currentTarget.style.background = '#242424')}
              >
                <td className="px-3 py-2 text-xs font-semibold text-[#F1F5F9]">{pos.symbol}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs font-semibold px-1.5 py-0.5 ${pos.side === 'Long' ? 'bg-[rgba(34,197,94,0.12)] text-[#22C55E]' : 'bg-[rgba(239,68,68,0.12)] text-[#EF4444]'}`}>
                    {pos.side} {pos.leverage}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs font-mono text-[#F1F5F9] tabular-nums">{pos.size}</td>
                <td className="px-3 py-2 text-xs font-mono text-[#94A3B8] tabular-nums">${pos.entryPrice}</td>
                <td className="px-3 py-2 text-xs font-mono text-[#F1F5F9] tabular-nums">${pos.markPrice}</td>
                <td className="px-3 py-2 text-xs font-mono text-[#4c6ef4] tabular-nums">{pos.liquidation === '—' ? '—' : `$${pos.liquidation}`}</td>
                <td className="px-3 py-2">
                  <div>
                    <span className={`text-xs font-mono font-semibold tabular-nums ${pos.positive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>{pos.pnl}</span>
                    <span className={`block text-[10px] font-mono tabular-nums ${pos.positive ? 'text-[#22C55E]' : 'text-[#EF4444]'}`}>{pos.pnlPct}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => handleClose(pos.id)}
                    disabled={closingId === pos.id}
                    className="px-2.5 py-1 text-xs font-medium bg-[rgba(239,68,68,0.1)] text-[#EF4444] border border-[rgba(239,68,68,0.2)] hover:bg-[rgba(239,68,68,0.2)] transition-all disabled:opacity-50"
                  >
                    {closingId === pos.id ? 'Closing...' : 'Close'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}